import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { Box, IconButton, Button } from '@material-ui/core';

import { Typography, Label, Select, SelectProps, Icon, TextField } from '@components';

import { DropEditProps } from './DropEdit.props';
import { useStyles } from './DropEdit.styles';

/**
 * DropEdit component
 */
export const DropEdit = ({
  className = '',
  uiType = 'primary',
  type = 'select',
  mode = 'idle',
  disabled = false,
  label = '',
  options = [],
  value = 0,
  onToggle,
  onChange,
  onTextChange,
}: DropEditProps): JSX.Element => {
  const styles = useStyles();
  const [text, setText] = useState(label);

  const valueLabel = useMemo(() => options.find((opt) => opt.id === value)?.label, [options, value]);

  const handleModeToggle = useCallback(() => {
    onToggle(mode === 'edit' ? 'idle' : 'edit');
  }, [onToggle, mode]);

  const handleSelectChange = useCallback(
    (event: ChangeEvent<{ value: number }>) => {
      if (onChange) {
        onChange(event.target.value);
      }
    },
    [onChange],
  ) as SelectProps['onChange'];

  const handleEditResult = useCallback(() => {
    if (onTextChange) {
      onTextChange(text);
    }
  }, [onTextChange, text]);

  const handleTextFieldChange = useCallback((val: string) => setText(val), [setText]);

  const handleTextFieldConfirm = useCallback(() => {
    handleModeToggle();
    handleEditResult();
  }, [handleModeToggle, handleEditResult]);

  return (
    <Box className={`${styles.dropEdit} ${className}`} data-ui-type={uiType}>
      {type === 'select' && label && (
        <Typography.SmallCaption className={styles.label}>{label}</Typography.SmallCaption>
      )}
      {type === 'textField' && label && mode === 'idle' && (
        <Typography.Headline className={styles.label}>{label}</Typography.Headline>
      )}
      <Box className={type === 'select' ? styles.body : styles.bodyTextField}>
        {mode === 'idle' && (
          <>
            {uiType === 'primary' && valueLabel && <Typography.Display>{valueLabel}</Typography.Display>}
            {uiType === 'secondary' && valueLabel && <Label variant="badge">{valueLabel}</Label>}
            <IconButton
              className={styles.edit}
              size="small"
              disabled={disabled}
              onClick={disabled ? undefined : handleModeToggle}
            >
              <Icon.Edit />
            </IconButton>
          </>
        )}
        {mode === 'edit' && type === 'select' && (
          <>
            <Select
              className={styles.select}
              variant="outlined"
              disabled={disabled}
              options={options}
              value={value}
              onChange={disabled ? undefined : handleSelectChange}
            />
            <Button
              className={styles.confirm}
              variant="outlined"
              size="small"
              disabled={disabled}
              onClick={disabled ? undefined : handleModeToggle}
            >
              <Icon.Checkmark />
            </Button>
          </>
        )}
        {mode === 'edit' && type === 'textField' && (
          <>
            <TextField
              disabled={disabled}
              value={text}
              className={styles.textField}
              onChange={disabled ? undefined : handleTextFieldChange}
            />
            <Button
              className={styles.confirmTextField}
              variant="outlined"
              size="small"
              disabled={disabled}
              onClick={disabled ? undefined : handleTextFieldConfirm}
            >
              <Icon.Checkmark />
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};
